import { without } from "lodash";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { CampaignStatus, ThreadSortBy } from "~frontend/__generated__/graphql";

import { OptOutFilter } from "../../routes/subaccounts/threads/types";

export function gqlValueForOptOutFilter(
  optOutFilter: OptOutFilter
): boolean | null {
  return {
    OPTED_OUT: true,
    NOT_OPTED_OUT: false,
    ANY: null,
  }[optOutFilter];
}

type OptOutSort = {
  value: OptOutFilter;
  label: ReactNode;
};

export const OPT_OUT_FILTERS: OptOutSort[] = [
  {
    value: "ANY",
    label: (
      <FormattedMessage
        defaultMessage="All"
        description="Filter for all threads (opted out and not)"
      />
    ),
  },
  {
    value: "OPTED_OUT",
    label: (
      <FormattedMessage
        defaultMessage="Opted Out"
        description="Filter for opted out threads"
      />
    ),
  },
  {
    value: "NOT_OPTED_OUT",
    label: (
      <FormattedMessage
        defaultMessage="Not Opted Out"
        description="Filter for threads that are not opted out"
      />
    ),
  },
];

type ThreadSort = {
  value: ThreadSortBy;
  label: ReactNode;
};

export const THREAD_SORTS: ThreadSort[] = [
  {
    value: ThreadSortBy.LastMessageAtDesc,
    label: (
      <FormattedMessage
        defaultMessage="Latest Message, Newest First"
        description="Sort order for threads"
      />
    ),
  },
  {
    value: ThreadSortBy.LastMessageAtAsc,
    label: (
      <FormattedMessage
        defaultMessage="Latest Message, Oldest First"
        description="Sort order for threads"
      />
    ),
  },
];

type CampaignStatusFilter = {
  value: CampaignStatus;
  optionLabel: ReactNode;
  pillLabel: ReactNode;
};

export const NON_ARCHIVED_STATUS_FILTERS = without(
  Object.values(CampaignStatus),
  CampaignStatus.Archived
);

export const CAMPAIGN_STATUS_FILTERS: CampaignStatusFilter[] = [
  {
    value: CampaignStatus.Draft,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Draft"
        description="Draft campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Draft"
        description="Draft campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.InitialSendReady,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Ready to Send"
        description="Ready to send campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Ready to Send"
        description="Ready to send campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.InitialSendScheduled,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Send Scheduled"
        description="Send scheduled campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Send Scheduled"
        description="Send scheduled campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.InitialSendCanceling,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Canceling Send"
        description="Canceling send campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Canceling Send"
        description="Canceling send campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.InitialSendComplete,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Send Complete"
        description="Send complete campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Send Complete"
        description="Send complete campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.InitialSendRunning,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Sending"
        description="Sending campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Sending"
        description="Sending campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.RoiTestAutosendPending,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Waiting For ROI Test"
        description="Roi test pending campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Waiting For ROI Test"
        description="Roi test pending campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.PartialSendEvaluationPending,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Evaluating Partial Send"
        description="Partial send evaluation pending campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Evaluating Partial Send"
        description="Partial send evaluation pending campaign status pill label"
      />
    ),
  },
  {
    value: CampaignStatus.Archived,
    optionLabel: (
      <FormattedMessage
        defaultMessage="Archived"
        description="Archived campaign status"
      />
    ),
    pillLabel: (
      <FormattedMessage
        defaultMessage="Campaign Archived"
        description="Archived campaign status pill label"
      />
    ),
  },
];

export const ALL_CAMPAIGN_STATUSES = Object.values(
  CampaignStatus
) as CampaignStatus[];
